import React, { Fragment, useState, useMemo } from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { abyssColors } from '@/theme/colors';

type DataItem = {
  [key: string]: number | string; // Add this to allow dynamic keys
};

type DisplayConfigItem = {
  fieldValue: string;
  color: string;
};

type DynamicCounterProps = {
  name: string;
  operation: string;
  data: DataItem[];
  displayConfig?: DisplayConfigItem[];
};

export const DynamicCounter: React.FC<DynamicCounterProps> = ({
  name,
  operation,
  data,
  displayConfig = [],
}) => {
  const isCountOperation = operation === 'count';

  const items = useMemo(() => {
    if (isCountOperation && data.length === 1) {
      return [
        {
          label: name,
          count: data[0][name],
          selected: false,
          color: '#000',
        },
      ];
    } else {
      return data.map((item) => ({
        ...item,
        label: item._id,
        count: item.count ?? 0,
        selected: false,
        color:
          displayConfig.find(
            (config) => config.fieldValue.toUpperCase() === item?._id?.toString().toUpperCase()
          )?.color ?? '#000',
      }));
    }
  }, [data, displayConfig, isCountOperation, name]);

  const [selectedIndex, setSelectedIndex] = useState<number | null | undefined>();

  const handleClick = (index: number) => {
    setSelectedIndex((previousIndex) => (previousIndex === index ? undefined : index));
  };
  if (data.length === 0)
    return (
      <Typography
        variant="h6"
        sx={{
          color: abyssColors.primary[400],
          whiteSpace: 'nowrap',
          mt: 1,
        }}
      >
        No data found
      </Typography>
    );
  return (
    <Stack direction="row" alignItems="left">
      {items.map(({ count, label, color }, index) => {
        const isLastItem = index !== items.length - 1;
        const isSelected = selectedIndex === index;

        return (
          <Fragment key={label}>
            <Stack onClick={() => handleClick(index)} sx={{ cursor: 'pointer' }}>
              <Typography
                variant="h4"
                sx={{ color }}
                data-testid={`summary-${label.toString().toLowerCase()}-count`}
              >
                {count}
              </Typography>
              {items.length > 1 && (
                <Typography fontSize="1.2rem" sx={{ color: '#425265', textAlign: 'center' }}>
                  {label}
                </Typography>
              )}
              {isSelected && (
                <Box
                  sx={{
                    width: '80%',
                    mt: 1.5,
                    height: 4,
                    borderBottomWidth: 5,
                    borderRadius: '8px',
                    backgroundColor: color,
                  }}
                />
              )}
            </Stack>
            {isLastItem && <Divider orientation="vertical" flexItem sx={{ mx: '7%' }} />}
          </Fragment>
        );
      })}
    </Stack>
  );
};
